// audio.util.ts - WebM audio blob merging implementation

/**
 * Merges multiple WebM audio blobs into a single WebM blob
 */
async function mergeAudioBlobs(blobArray: Blob[]): Promise<Blob> {
  // Filter out empty blobs - moved outside try/catch for scope access
  const validBlobs = blobArray?.filter(blob => blob && blob.size > 0) || [];
  
  try {
    // Input validation
    if (!blobArray || blobArray.length === 0) {
      console.error("No blobs provided to merge");
      throw new Error("No blobs provided to merge");
    }
    
    if (validBlobs.length === 0) {
      console.error("All provided blobs were empty");
      throw new Error("All provided blobs were empty");
    }
    
    // If only one valid blob, return it directly
    if (validBlobs.length === 1) {
      console.log("Only one valid blob found, returning it directly");
      return validBlobs[0];
    }

    // Log blob sizes for debugging
    validBlobs.forEach((blob, index) => {
      console.log(`Blob ${index} size: ${blob.size} bytes, type: ${blob.type}`);
    });
    
    // Simple approach: concatenate WebM blobs directly
    // This assumes the blobs are WebM format with compatible codecs
    // For proper WebM merging, headers might need special handling
    const mediaType = validBlobs[0].type || 'audio/webm';
    return new Blob(validBlobs, { type: mediaType });
  } catch (error) {
    console.error("Failed to merge audio blobs:", error);
    
    // If merging fails, return the first blob or an empty blob
    return validBlobs[0] || new Blob([], { type: 'audio/webm' });
  }
}

/**
 * Converts a blob to a File object
 */
export function blobToFile(blob: Blob, filename: string = 'audio.webm'): File {
  return new File([blob], filename, { type: blob.type });
}

/**
 * Converts a blob to a data URL for storage
 */
export function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Converts a data URL back to a Blob
 */
export function dataURLToBlob(dataURL: string): Blob {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'audio/webm';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  
  return new Blob([u8arr], { type: mime });
}

export default mergeAudioBlobs;